import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Logo } from '@/components/Logo';

export function ContactPage() {
  const { t } = useTranslation('contact');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(t('mailtoSubject', { name }));
    const body = encodeURIComponent(t('mailtoBody', { name, email, message }));
    window.location.href = `mailto:support@agentcad.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <div className="min-h-dvh bg-k-canvas">
      <nav className="border-b-2 border-brand-indigo bg-k-canvas/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo className="h-9 w-9 text-brand-indigo" />
            <span className="font-display text-lg font-bold tracking-tight text-brand-indigo">
              AgentCAD
            </span>
          </Link>
          <LanguageSwitcher />
        </div>
      </nav>

      <main className="mx-auto max-w-2xl px-4 py-12 sm:px-6 sm:py-16">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-1.5 text-sm font-bold text-brand-indigo transition-colors hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('backToTop')}
        </Link>

        <div className="rounded-2xl border-2 border-brand-indigo/15 bg-k-card p-6 sm:p-10">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-brand-indigo sm:text-4xl">
            {t('title')}
          </h1>
          <p className="mt-3 text-base leading-relaxed text-k-ink/80">{t('description')}</p>

          {submitted ? (
            <div className="mt-10 rounded-xl border-2 border-brand-indigo bg-brand-teal-soft p-8 text-center">
              <p className="font-display text-base font-bold text-brand-indigo">
                {t('submittedMessage')}
              </p>
              <Button
                onClick={() => setSubmitted(false)}
                variant="outline"
                className="mt-5"
                size="sm"
              >
                {t('sendAgain')}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-10 space-y-5">
              <div>
                <label
                  htmlFor="name"
                  className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-brand-indigo"
                >
                  {t('nameLabel')}
                </label>
                <Input
                  id="name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('namePlaceholder')}
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-brand-indigo"
                >
                  {t('emailLabel')}
                </label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('emailPlaceholder')}
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-brand-indigo"
                >
                  {t('messageLabel')}
                </label>
                <textarea
                  id="message"
                  required
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('messagePlaceholder')}
                  className="w-full rounded-lg border-[1.5px] border-k-line bg-k-card px-3 py-2 text-base text-k-ink placeholder:text-k-ink-faint hover:border-k-line-strong focus:border-brand-indigo focus:outline-none"
                />
              </div>

              <Button type="submit" className="w-full" size="lg">
                <Send className="mr-2 h-4 w-4" />
                {t('submit')}
              </Button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
